// --- Style Preset System (ADR-017) ---

export interface StylePreset {
  id: string;
  category: string;
  label: string;
  styleString: string;
  type: 'genre' | 'instrument';
}

export const GENRE_PRESETS: readonly StylePreset[] = [
  // Cinematic
  {
    id: 'cinematic-epic',
    category: 'Cinematic',
    label: 'Epic Score',
    styleString: 'epic cinematic orchestral score with sweeping dynamics',
    type: 'genre',
  },
  {
    id: 'cinematic-intimate',
    category: 'Cinematic',
    label: 'Intimate Underscore',
    styleString: 'intimate cinematic underscore with subtle emotional depth',
    type: 'genre',
  },
  {
    id: 'cinematic-dark',
    category: 'Cinematic',
    label: 'Dark Tension',
    styleString: 'dark cinematic tension with ominous undertones',
    type: 'genre',
  },

  // Ambient
  {
    id: 'ambient-ethereal',
    category: 'Ambient',
    label: 'Ethereal Soundscape',
    styleString: 'ethereal ambient soundscape with shimmering textures',
    type: 'genre',
  },
  {
    id: 'ambient-warm',
    category: 'Ambient',
    label: 'Warm Pads',
    styleString: 'warm ambient pads with subtle movement and gentle drift',
    type: 'genre',
  },
  {
    id: 'ambient-dark',
    category: 'Ambient',
    label: 'Dark Drone',
    styleString: 'dark ambient drone with deep atmospheric layers',
    type: 'genre',
  },

  // Folk
  {
    id: 'folk-gentle',
    category: 'Folk',
    label: 'Gentle Acoustic',
    styleString: 'gentle folk with acoustic instruments and warm tone',
    type: 'genre',
  },
  {
    id: 'folk-upbeat',
    category: 'Folk',
    label: 'Upbeat Stomp',
    styleString: 'upbeat folk with hand claps and rhythmic stomp',
    type: 'genre',
  },

  // Rock
  {
    id: 'rock-soft',
    category: 'Rock',
    label: 'Soft Rock',
    styleString: 'soft rock with mellow electric guitar and steady rhythm',
    type: 'genre',
  },
  {
    id: 'rock-driving',
    category: 'Rock',
    label: 'Driving Rock',
    styleString: 'driving rock with powerful riffs and strong backbeat',
    type: 'genre',
  },

  // Electronic
  {
    id: 'electronic-chill',
    category: 'Electronic',
    label: 'Chill Electronic',
    styleString: 'chill electronic with soft synth pads and gentle beat',
    type: 'genre',
  },
  {
    id: 'electronic-pulsing',
    category: 'Electronic',
    label: 'Pulsing Synth',
    styleString: 'pulsing electronic synth with rhythmic energy and movement',
    type: 'genre',
  },

  // Orchestral
  {
    id: 'orchestral-full',
    category: 'Orchestral',
    label: 'Full Orchestra',
    styleString: 'full orchestral ensemble with rich harmonies and dynamic range',
    type: 'genre',
  },
  {
    id: 'orchestral-chamber',
    category: 'Orchestral',
    label: 'Chamber Ensemble',
    styleString: 'intimate chamber ensemble with delicate interplay',
    type: 'genre',
  },

  // Jazz
  {
    id: 'jazz-smooth',
    category: 'Jazz',
    label: 'Smooth Jazz',
    styleString: 'smooth jazz with mellow saxophone and soft rhythm section',
    type: 'genre',
  },
  {
    id: 'jazz-lounge',
    category: 'Jazz',
    label: 'Lounge',
    styleString: 'relaxed jazz lounge with piano trio and brushed drums',
    type: 'genre',
  },

  // Classical
  {
    id: 'classical-romantic',
    category: 'Classical',
    label: 'Romantic Period',
    styleString: 'romantic classical with expressive melody and lush harmony',
    type: 'genre',
  },
  {
    id: 'classical-minimalist',
    category: 'Classical',
    label: 'Minimalist',
    styleString: 'minimalist classical with repetitive patterns and gradual evolution',
    type: 'genre',
  },

  // Lo-fi
  {
    id: 'lofi-chill',
    category: 'Lo-fi',
    label: 'Chill Lo-fi',
    styleString: 'chill lo-fi hip hop with vinyl crackle and mellow beat',
    type: 'genre',
  },
  {
    id: 'lofi-jazzy',
    category: 'Lo-fi',
    label: 'Jazzy Lo-fi',
    styleString: 'jazzy lo-fi with warm keys and laid-back groove',
    type: 'genre',
  },
] as const;

export const INSTRUMENT_PRESETS: readonly StylePreset[] = [
  // Piano
  {
    id: 'piano-soft',
    category: 'Piano',
    label: 'Soft Arpeggios',
    styleString: 'soft piano with gentle arpeggios and delicate touch',
    type: 'instrument',
  },
  {
    id: 'piano-dramatic',
    category: 'Piano',
    label: 'Dramatic Chords',
    styleString: 'dramatic piano with powerful chords and dynamic intensity',
    type: 'instrument',
  },
  {
    id: 'piano-minimalist',
    category: 'Piano',
    label: 'Sparse Minimalist',
    styleString: 'minimalist piano with sparse notes and open space',
    type: 'instrument',
  },

  // Guitar
  {
    id: 'guitar-warm-fingerpicking',
    category: 'Guitar',
    label: 'Warm Fingerpicking',
    styleString: 'warm acoustic guitar with light fingerpicking',
    type: 'instrument',
  },
  {
    id: 'guitar-rhythmic-strumming',
    category: 'Guitar',
    label: 'Rhythmic Strumming',
    styleString: 'rhythmic acoustic guitar strumming with steady pulse',
    type: 'instrument',
  },
  {
    id: 'guitar-clean-arpeggios',
    category: 'Guitar',
    label: 'Clean Arpeggios',
    styleString: 'clean electric guitar arpeggios with subtle reverb',
    type: 'instrument',
  },

  // Strings
  {
    id: 'strings-lush-ensemble',
    category: 'Strings',
    label: 'Lush Ensemble',
    styleString: 'lush string ensemble with sweeping legato and warm vibrato',
    type: 'instrument',
  },
  {
    id: 'strings-solo-cello',
    category: 'Strings',
    label: 'Solo Cello',
    styleString: 'intimate solo cello with expressive phrasing',
    type: 'instrument',
  },
  {
    id: 'strings-pizzicato',
    category: 'Strings',
    label: 'Pizzicato',
    styleString: 'pizzicato strings with playful energy and light bounce',
    type: 'instrument',
  },

  // Drums
  {
    id: 'drums-brushed',
    category: 'Drums',
    label: 'Brushed Snare',
    styleString: 'gentle percussion with brushed snare and soft hi-hat',
    type: 'instrument',
  },
  {
    id: 'drums-driving',
    category: 'Drums',
    label: 'Driving Pulse',
    styleString: 'driving drums with steady pulse and strong kick',
    type: 'instrument',
  },
  {
    id: 'drums-minimal',
    category: 'Drums',
    label: 'Subtle Accents',
    styleString: 'minimal percussion with subtle accents and restrained dynamics',
    type: 'instrument',
  },

  // Synth
  {
    id: 'synth-warm-pads',
    category: 'Synth',
    label: 'Warm Pads',
    styleString: 'warm synthesizer pads with slow evolving textures',
    type: 'instrument',
  },
  {
    id: 'synth-bright-lead',
    category: 'Synth',
    label: 'Bright Lead',
    styleString: 'bright synth lead with clean tone and gentle modulation',
    type: 'instrument',
  },

  // Brass
  {
    id: 'brass-french-horns',
    category: 'Brass',
    label: 'French Horns',
    styleString: 'majestic French horns with warm sustained notes',
    type: 'instrument',
  },
  {
    id: 'brass-muted-trumpet',
    category: 'Brass',
    label: 'Muted Trumpet',
    styleString: 'muted trumpet with intimate jazz phrasing',
    type: 'instrument',
  },

  // Woodwinds
  {
    id: 'woodwinds-flute',
    category: 'Woodwinds',
    label: 'Gentle Flute',
    styleString: 'gentle flute with airy tone and flowing melody',
    type: 'instrument',
  },
  {
    id: 'woodwinds-clarinet',
    category: 'Woodwinds',
    label: 'Warm Clarinet',
    styleString: 'warm clarinet with smooth legato and rich lower register',
    type: 'instrument',
  },
] as const;

export const STYLE_PRESETS: readonly StylePreset[] = [...GENRE_PRESETS, ...INSTRUMENT_PRESETS];

// --- Tones ---

export const TONES = [
  'inspiring',
  'somber',
  'energetic',
  'calm',
  'tense',
  'hopeful',
  'dramatic',
  'reflective',
] as const;
export type Tone = (typeof TONES)[number];

export const TONE_NEGATIVES: Record<Tone, string> = {
  inspiring: 'dark, somber, pessimistic, heavy, gloomy',
  somber: 'bright, uplifting, energetic, cheerful, fast',
  energetic: 'calm, gentle, slow, quiet, minimal',
  calm: 'energetic, fast-paced, loud, driving, intense',
  tense: 'peaceful, relaxed, gentle, warm, comforting',
  hopeful: 'dark, ominous, aggressive, harsh, despairing',
  dramatic: 'minimal, subdued, quiet, gentle, understated',
  reflective: 'energetic, loud, aggressive, fast-paced, driving',
};

// Colors chosen for WCAG AA contrast with white text (4.5:1+) and
// distinguishable under protanopia/deuteranopia (no red-green reliance).
export const TONE_COLORS: Record<Tone, { bg: string; fg: string }> = {
  inspiring: { bg: '#a3551a', fg: '#ffffff' }, // Dark amber
  somber: { bg: '#4a5666', fg: '#ffffff' }, // Slate
  energetic: { bg: '#ca3838', fg: '#ffffff' }, // Red
  calm: { bg: '#167eb3', fg: '#ffffff' }, // Sky blue
  tense: { bg: '#814cdb', fg: '#ffffff' }, // Purple
  hopeful: { bg: '#1b6d83', fg: '#ffffff' }, // Teal (blue-shifted, CVD-safe)
  dramatic: { bg: '#ad2960', fg: '#ffffff' }, // Rose/magenta
  reflective: { bg: '#4f47bb', fg: '#ffffff' }, // Deep indigo
};

// --- Energy ---

export const ENERGY_MIN = 1;
export const ENERGY_MAX = 10;
export const ENERGY_DEFAULT = 5;

// --- Tone Styles (for composition plan derivation) ---

export const TONE_STYLES: Record<string, string> = {
  inspiring: 'uplifting, motivational, bright, soaring, gradually building energy',
  somber: 'melancholic, slow, minor key, contemplative, somber',
  energetic: 'fast-paced, driving, high energy, punchy, intense',
  calm: 'ambient, peaceful, gentle, meditative, soft',
  tense: 'suspenseful, dark, building tension, ominous, uneasy',
  hopeful: 'warm, optimistic, gradually building, tender, hopeful',
  dramatic: 'cinematic, powerful, sweeping, epic, dramatic',
  reflective: 'contemplative, soft, introspective, nostalgic, reflective',
};

const DEFAULT_STYLE = 'moderate, balanced, neutral, smooth';

export function energyToBpm(energy: number): number {
  return Math.min(150, Math.max(60, 55 + energy * 10));
}

// --- Composition Plan Derivation ---

export function derivePositiveGlobalStyles(
  selectedPresets: StylePreset[],
  energy: number,
): string[] {
  const styles: string[] = ['instrumental'];
  for (const preset of selectedPresets) {
    styles.push(preset.styleString);
  }
  styles.push(`${energyToBpm(energy)} BPM`);
  return styles;
}

export function deriveNegativeGlobalStyles(
  selectedPresets: StylePreset[],
  aiSuggestedNegatives?: string[],
): string[] {
  const negatives: string[] = ['vocals', 'singing', 'lyrics'];

  // If AI provided targeted negatives, use those instead of all unselected categories
  if (aiSuggestedNegatives && aiSuggestedNegatives.length > 0) {
    negatives.push(...aiSuggestedNegatives);
    return negatives;
  }

  const selectedGenreCategories = new Set(
    selectedPresets.filter((p) => p.type === 'genre').map((p) => p.category),
  );
  for (const preset of GENRE_PRESETS) {
    if (!selectedGenreCategories.has(preset.category)) {
      const lowerCategory = preset.category.toLowerCase();
      if (!negatives.includes(lowerCategory)) {
        negatives.push(lowerCategory);
      }
    }
  }

  const selectedInstrumentCategories = new Set(
    selectedPresets.filter((p) => p.type === 'instrument').map((p) => p.category),
  );
  for (const preset of INSTRUMENT_PRESETS) {
    if (!selectedInstrumentCategories.has(preset.category)) {
      const lowerCategory = preset.category.toLowerCase();
      if (!negatives.includes(lowerCategory)) {
        negatives.push(lowerCategory);
      }
    }
  }

  return negatives;
}

export function derivePositiveLocalStyles(tone: string, energy: number): string[] {
  const toneStyles = TONE_STYLES[tone] ?? DEFAULT_STYLE;
  return [toneStyles, `${energyToBpm(energy)} BPM`];
}

export function deriveNegativeLocalStyles(tone: string): string[] {
  const negatives = TONE_NEGATIVES[tone as Tone];
  if (!negatives) return [];
  return [negatives];
}

// --- Backward Compatibility ---
// Derived from preset categories for code that still references flat arrays.

function uniqueCategories(presets: readonly StylePreset[]): string[] {
  return [...new Set(presets.map((p) => p.category.toLowerCase()))];
}

export const GENRES = uniqueCategories(GENRE_PRESETS);
export type Genre = string;

export const INSTRUMENTS = uniqueCategories(INSTRUMENT_PRESETS);
export type Instrument = string;
